'use strict';

const dates = require('util/dates');

const uniqWith = require('lodash/uniqWith');
const groupBy = require('lodash/groupBy');
const forEach = require('lodash/forEach');
const intersectionWith = require('lodash/intersectionWith');
const orderBy = require('lodash/orderBy');
const map = require('lodash/map');
const isString = require('lodash/isString');
const find = require('lodash/find');
const cloneDeep = require('lodash/cloneDeep');
const omit = require('lodash/omit');

module.exports = ['$location', '$filter', '$http', 'locale', 'appConfig', 'inventoryService', 'translationService', ($location, $filter, $http, locale, appConfig, inventoryService, translationService) => {
    const today = dates.moment();
    const tomorrow = today.clone().add(1, 'days');
    const sevenDays = dates.moment().add(7, 'days');
    const thirtyDays = dates.moment().add(30, 'days');
    const eveningFrom = 18;

    const currency = $filter('currency');

    // Set the fuse options for use elsewhere
    const fuseOptions = {
        shouldSort: false,
        threshold: 0.2,
        location: 0,
        distance: 100,
        maxPatternLength: 32,
        minMatchCharLength: 3,
        keys: [
            { name: 'title', weight: 0.35 },
            { name: 'prefix', weight: 0.20 },
            { name: 'suffix', weight: 0.20 },
            { name: 'facets.keyword', weight: 0.25 }
        ]
    };

    function getPerformances() {
        return inventoryService.getPerformances().then(response => {
            const performanceNoticeWebContentKey = translationService.translate('performance_message');

            return map(response, performance => {
                let facets = [];

                facetisePeriods(performance, facets);

                facetisePricing(performance, facets);

                facets = uniqWith(facets, (a, b) => {
                    return (
                        a.category === b.category &&
                        a.keyword === b.keyword
                    );
                });

                performance.facets = [].concat(performance.facets, facets);

                performance.is_relaxed = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'Relaxed Performance'
                    );
                });

                performance.is_chilled = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'Chilled Performance'
                    );
                });

                performance.is_signed = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'BSL Accessible'
                    );
                });

                performance.is_captioned = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'Captioned'
                    );
                });

                performance.is_closed_captioned = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'Closed Captioned'
                    );
                });

                performance.is_open_captioned = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'Open Captioned'
                    );
                });

                performance.is_audio_described = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'Audio Described'
                    );
                });

                performance.is_touch_tour = find(performance.facets, item => {
                    return (
                        item.category === 'Access Performance' &&
                        item.keyword === 'Touch Tour Available'
                    );
                });

                performance.has_accessibility = performance.is_signed ||
                    performance.is_captioned ||
                    performance.is_relaxed ||
                    performance.is_closed_captioned ||
                    performance.is_open_captioned ||
                    performance.is_audio_described ||
                    performance.is_touch_tour ||
                    performance.is_chilled;


                performance.flag = find(performance.web_contents, (value, key) => {
                    if (locale === 'cy') {
                        return key === 'Web Status Message Cymraeg';
                    }

                    return key === 'Web Status Message English';
                });

                performance.off_sale = performance.status === 2 || find(performance.web_contents, (value, key) => {
                    return (
                        key === 'Web Off Sale' &&
                        value.toLowerCase() === 'yes'
                    );
                });

                performance.status = find(performance.web_contents, (value, key) => {
                    if (locale === 'cy') {
                        return key === 'Web Booking Status Cymraeg';
                    }

                    return key === 'Web Booking Status English';
                });

                performance.notice = performanceNoticeWebContentKey in performance.web_contents && performance.web_contents ? performance.web_contents[performanceNoticeWebContentKey] : false;

                performance.sold_out = false;

                if (performance.availability === 0) {
                    performance.off_sale = true;
                    performance.sold_out = true;

                    performance.status_description = translationService.translate('sold_out');
                }

                if ('CMS_PERF_PRICE' in performance.web_contents && performance.web_contents.CMS_PERF_PRICE.length) {
                    performance.price_description = performance.web_contents.CMS_PERF_PRICE;
                } else if (performance.pricing.length) {
                    if (performance.min_price === performance.max_price) {
                        performance.price_description = currency(performance.min_price, '£');
                    } else {
                        performance.price_description = locale === 'en' ? `From ${currency(performance.min_price, '£')}` : `O ${currency(performance.min_price, '£')}`;
                    }
                }

                performance.min_price_description = currency(performance.min_price, '£');
                performance.max_price_description = currency(performance.max_price, '£');
                performance.date_display = performance.start_date_moment.format('h:mma');
                performance.date_class = 'performance-' + performance.start_date_moment.format('YYYY-MM-DD');

                performance.capacity = parseInt(performance.capacity_override, 10);

                if (!performance.capacity && 'venue_capacities' in appConfig && performance.facility_no in appConfig.venue_capacities) {
                    performance.capacity = appConfig.venue_capacities[performance.facility_no];
                }

                if (!performance.off_sale && performance.capacity) {
                    if (performance.availability === performance.accessible_availability) {
                        performance.availability_label = translationService.translate('accessible_availability');
                        performance.availability_class = 'limited';
                    } else {
                        const availability = (performance.availability / performance.capacity) * 100;

                        if (availability < 10) {
                            performance.availability_label = translationService.translate('low_availability');
                            performance.availability_class = 'limited';
                        } else if (availability < 40) {
                            performance.availability_label = translationService.translate('medium_availability');
                            performance.availability_class = 'medium';
                        } else {
                            performance.availability_label = translationService.translate('high_availability');
                            performance.availability_class = 'high';
                        }
                    }
                }

                return performance;
            });
        });
    }

    function getEvents() {
        return inventoryService.getEvents().then(response => {
            return map(response, event => {
                let facets = [];

                forEach(event.performances, performance => {
                    facetisePeriods(performance, facets);
                    facetisePricing(performance, facets);
                });

                facets = uniqWith(facets, (a, b) => {
                    return (
                        a.category === b.category &&
                        a.keyword === b.keyword
                    );
                });

                event.facets = [].concat(event.facets, facets);

                return event;
            });
        });
    }

    /* eslint-disable */
    function getFoodAndDrinkPerformances() {
        return $http.get('/examples/data/booking-widget-feed.json');
    }
    /* eslint-enable */

    function facetisePeriods(item, facets) {
        const date = item.start_date_moment;

        if (date.isBefore(sevenDays)) {
            facets.push({
                category: 'Day',
                title: locale === 'en' ? 'Next 7 Days' : '7 Diwrnod Nesaf',
                keyword: 'Next 7 Days',
                icon: '#icon-next-7-days',
                sort: 0
            });
        }

        if (date.isBefore(thirtyDays)) {
            facets.push({
                category: 'Day',
                keyword: locale === 'en' ? 'Next 30 Days' : '30 Diwrnod Nesaf',
                icon: '#icon-next-30-days',
                sort: 1
            });
        }

        const day = date.format('d');

        if (day === '0' || day === '6') {   // Saturday/Sunday
            facets.push({
                category: 'Day',
                keyword: locale === 'en' ? 'Weekends' : 'Penwythnosau',
                icon: '#icon-weekends',
                sort: 2
            });
        }

        if (date.format('HH') < eveningFrom) {
            facets.push({
                category: 'Period',
                keyword: 'Daytime',
                icon: '#icon-daytime',
                sort: 0
            });
        } else {
            facets.push({
                category: 'Period',
                keyword: 'Evening',
                icon: '#icon-evening',
                sort: 1
            });
        }

        return facets;
    }

    function facetisePricing(item, facets) {
        if (!item.pricing.length) {
            facets.push({
                category: 'Price',
                title: translationService.translate('price_free'),
                keyword: 'Free',
                sort: 0,
            });

            return facets;
        }

        forEach(item.pricing, price => {
            if (price <= 10) {
                facets.push({
                    category: 'Price',
                    keyword: 'Under £10',
                    sort: 1
                });
            } else if (price > 10 && price <= 25) {
                facets.push({
                    category: 'Price',
                    keyword: '£10 – £25',
                    sort: 2
                });
            } else if (price > 25 && price <= 50) {
                facets.push({
                    category: 'Price',
                    keyword: '£25 – £50',
                    sort: 3
                });
            } else if (price > 50 && price <= 75) {
                facets.push({
                    category: 'Price',
                    keyword: '£50 – £75',
                    sort: 4
                });
            } else if (price > 75) {
                facets.push({
                    category: 'Price',
                    keyword: 'Over £75',
                    sort: 5
                });
            }
        });

        return facets;
    }

    function generateFacets(performances, filters) {
        let facets = performances.reduce((carry, performance) => {
            return [].concat(carry, performance.facets);
        }, []);

        facets = groupBy(facets, 'category');

        filters = filters || [
            { category: 'Period', title: translationService.translate('filter_time_of_day'), canClear: false },
            { category: 'WMC Website Interests', title: translationService.translate('filter_genre'), canClear: false },
            { category: 'Price', title: translationService.translate('filter_price'), canClear: false },
            { category: 'Access Performance', title: translationService.translate('filter_accessibility'), canClear: false }
        ];

        return filters.map(filter => {
            const items = facets[filter.category];

            let options = groupBy(items, 'keyword');

            options = map(options, keywords => {
                const keyword = keywords[0];

                keyword.id = filter.category + '-' + keyword.keyword;
                keyword.checked = false;
                keyword.disabled = false;
                keyword.count = keywords.length;

                let key = 'keyword_' + keyword.keyword.toLowerCase().replace(/ /g, "_");
                let translation = translationService.translate(key);

                keyword.title = (!translation) ? keyword.keyword : translation;

                return keyword;
            });

            filter.keywords = orderBy(options, item => {
                if ('sort' in item) {
                    return item.sort;
                }

                return item.keyword;
            });

            return filter;
        });
    }

    function filterAndUpdate(allResults, groupedFacets, updateLocation) {
        // Firstly, lets create a local copy of all results so we can
        // mutate the thing
        let filteredResults = allResults;

        // We'll keep track of all applied facets for use later to determine
        // future states of facets by applied the currently applied facets
        // and applying but not saving another facet
        let appliedFacets = {};

        // Keep track of all facet names
        let allFacets = [];

        // But before we can do that, loop through the things
        groupedFacets.forEach(group => {
            // Assume nothing is applied...
            group.canClear = false;

            // And grab the applied facets in the current group, because we need to
            // do an OR using everything from current group, so we intersect
            // everything at once from this group
            const appliedGroupFacets = group.keywords.filter(option => {
                return option.checked === true;
            });

            // Add to the global knowledge
            allFacets.push(group.category);

            // But of course, if there is nothing to apply, there's nothing to do.
            if (appliedGroupFacets.length === 0) {
                return;
            }

            // If options are applied from this group then it's clearable!
            group.canClear = true;

            // Do the applying by filtering through the results and updating itself
            filteredResults = filteredResults.filter(result => {
                // Intersection will return an array if anything matches, so we'll
                // simply return the length since it'll evaluate to a boolean
                return applyFacets(result.facets, appliedGroupFacets).length;
            });

            // Add the current group to the global groups for use later
            appliedFacets[group.category] = appliedGroupFacets;
        });

        // So now we have a filtered list, and a list of applied facets. We can
        // go through and update the states of our other facets to ensure the
        // correct counts and disabled states are set. The aim is to ensure the
        // user can never end up with an empty result set, so outright prevent
        // them from clicking things which won't yield anything.

        // To do so, we'll go through the groups again
        groupedFacets.forEach(group => {
            // Keep track of the category for ease of access later
            const category = group.category;

            // But this time, we'll also go through the options
            group.keywords.forEach(option => {
                // We've already dealt with a checked option by filtering items
                // earlier so we can skip it here.
                if (option.checked === true) {
                    return;
                }

                // The aim is to determine a future state of the planner, so we
                // need to assume that the user potentially may apply this option
                // so we'll need to let the user know how many items will be there
                // if this option has been selected, therefore we'll merge it to
                // our existing list of facets.

                // To do that, we start with a cloned copy of our applied facets
                let potentialFacets = cloneDeep(appliedFacets);

                // Then we either add the current option to already existing
                // array or create a new one with the new item
                if (category in potentialFacets) {
                    potentialFacets[category].push(option);
                } else {
                    potentialFacets[category] = [option];
                }

                // We always start with all items
                let potentialResults = allResults;

                // Apply the facets, one group at a time, but we're only mutating
                // our temporary object so we're not fucking up our actual results
                forEach(potentialFacets, facets => {
                    // Potential results IF current facet is applied
                    potentialResults = potentialResults.filter(result => {
                        // Same principle as before
                        return applyFacets(result.facets, facets).length;
                    });
                });

                // Grab all the facets from the potential future results...
                potentialFacets = potentialResults.reduce((carry, result) => {
                    return [].concat(carry, result.facets);
                }, []);

                // ...ut we only want the current option
                potentialFacets = potentialFacets.filter(facet => {
                    return (
                        facet.category === option.category &&
                        facet.keyword === option.keyword
                    );
                });

                // Update to the "would-be" total
                option.count = potentialFacets.length;

                // Will the future state mean empty results?
                option.disabled = !option.count;
            });
        });

        // Also call the fuction to update the query strings if required
        // using our applied facets object from earlier.
        if (updateLocation === true) {
            updateQueryString(appliedFacets, allFacets);
        }

        // Finally. return the filtered list we came up with way back
        return filteredResults;
    }

    function updateQueryStringKeyword(keyword) {
        // Grab the queries from the URL
        let query = $location.search();

        // Determine if we actually have any params
        if (!query || !Object.keys(query).length) {
            query = {};
        }

        // Do we have a keyword?
        if (isString(keyword) && keyword.length) {
            query.keyword = keyword;
        } else {
            delete query.keyword;
        }

        // Update the URL using query object
        $location.search(query);
    }

    function applyFacets(itemFacets, facets) {
        return intersectionWith(itemFacets, facets, (a, b) => {
            return (
                a.category === b.category &&
                a.keyword === b.keyword
            );
        });
    }

    function groupByMonth(items) {
        let groupedItems = groupBy(items, item => {
            return item.start_date_moment.clone().endOf('month').format();
        });

        groupedItems = map(groupedItems, (group, key) => {
            return {
                title: dates.moment(key).format('MMMM YYYY'),
                sort: key,
                items: group
            };
        });

        // Also sort :3
        return orderBy(groupedItems, 'sort');
    }

    function groupLabelDay(date) {
        var dt = dates.moment(date);

        if (dt.isSame(today, 'day')) {
            return 'Today';
        }

        if (dt.isSame(tomorrow, 'day')) {
            return 'Tomorrow';
        }

        if (dt.year() === today.year()) {
            return dt.format('ddd D MMMM');
        }

        return dt.format('ddd D MMMM, YYYY');
    }

    function groupByDay(items) {
        var groupedItems = groupBy(items, item => {
            return item.start_date_moment.clone().startOf('day').format();
        })

        return map(groupedItems, (group, key) => {
            const dt = dates.moment(key);
            return {
                title: groupLabelDay(key),
                slug: dt.format('YYYY-MM-DD'),
                sort: key,
                items: group
            };
        })
    }

    function updateQueryString(appliedFacets, allFacets) {
        // Grab the queries from the URL
        let query = $location.search();

        // Determine if we actually have any params
        if (!query || !Object.keys(query).length) {
            query = {};
        }

        // Get rid of facets so we don't end up accidentally retaining them
        query = omit(query, allFacets);

        // Loop through our group of facets
        forEach(appliedFacets, (options, group) => {
            // Grab the name of the option and sticl it in the query object
            query[group] = options.map(option => {
                return option.keyword;
            });
        })

        // Update the URL using query object
        $location.search(query);
    }

    function applyQueryString(groupedFacets) {
        // Grab the queries from the URL
        let query = $location.search();

        // Determine if we actually have any params
        if (!query || !Object.keys(query).length) {
            query = {};
        }

        // Loop through our grouped facets
        groupedFacets.forEach(group => {
            // Start with empty array
            let appliedFacets = [];

            // Grab a list of applied factes for this group if there are
            // any assets applied for this group
            if (group.category in query) {
                appliedFacets = query[group.category];
            }

            // Now loop through the options
            group.keywords.forEach(option => {
                // Check if the facet is applied. In any other language this
                // wouldn't work, because appliedFacets is either a string or
                // and array, but indexOf works exactly the same for both
                // arrays and strings so this can work a charm.
                option.checked = appliedFacets.indexOf(option.keyword) !== -1;
            })
        })

        let keyword = '';
        let selectedDate = null;

        // Do we have a keyword?
        if ('keyword' in query && isString(query.keyword) && query.keyword.length) {
            keyword = query['keyword'];
        }

        //  Do we have a date?
        if ('date' in query && isString(query.date) && query.date.length) {
            selectedDate = dates.moment(query.date);
        }

        return {
            keyword: keyword,
            selectedDate: selectedDate,
        }
    }

    return {
        fuseOptions: fuseOptions,
        getPerformances: getPerformances,
        getEvents: getEvents,
        generateFacets: generateFacets,
        filterAndUpdate: filterAndUpdate,
        applyQueryString: applyQueryString,
        updateQueryStringKeyword: updateQueryStringKeyword,
        groupByMonth: groupByMonth,
        groupByDay: groupByDay,
    };
}]



// WEBPACK FOOTER //
// ./widgets/services/plannerService.js